import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './queryClient';
import { businessUnitsService } from '@/services/businessUnits.service';
import {
  adjustmentsService,
  alertsService,
  auditService,
  commentsService,
  valuesService,
} from '@/services/collab.service';
import { companyService } from '@/services/company.service';
import { categoriesService } from '@/services/categories.service';
import { importsService } from '@/services/imports.service';
import { periodsService } from '@/services/periods.service';
import {
  extraordinaryExpensesService,
  type ExtraordinaryExpenseInput,
  type ExtraordinaryExpenseUpdateInput,
} from '@/services/extraordinaryExpenses.service';

export function useBusinessUnits() {
  return useQuery({
    queryKey: queryKeys.units,
    queryFn: ({ signal }) => businessUnitsService.list(signal),
  });
}

export function usePeriods() {
  return useQuery({
    queryKey: queryKeys.periods,
    queryFn: ({ signal }) => periodsService.list(signal),
  });
}

export function useKpis(unitId: string | undefined, periodId?: string) {
  return useQuery({
    queryKey: queryKeys.kpis(unitId ?? '', periodId),
    queryFn: ({ signal }) => businessUnitsService.kpis(unitId as string, periodId, signal),
    enabled: Boolean(unitId),
  });
}

export function usePnl(unitId: string | undefined, year: number) {
  return useQuery({
    queryKey: queryKeys.pnl(unitId ?? '', year),
    queryFn: ({ signal }) => businessUnitsService.pnl(unitId as string, year, signal),
    enabled: Boolean(unitId),
  });
}

/** The confirmed Excel report behind «Отчёт ОПУ». Analytics use `usePnl`. */
export function usePnlSource(unitId: string | undefined, year: number) {
  return useQuery({
    queryKey: ['pnl-source', unitId ?? '', year],
    queryFn: ({ signal }) => businessUnitsService.pnlSource(unitId as string, year, signal),
    enabled: Boolean(unitId),
    retry: false,
  });
}

export function useExpenses(unitId: string | undefined, periodId?: string) {
  return useQuery({
    queryKey: queryKeys.expenses(unitId ?? '', periodId),
    queryFn: ({ signal }) => businessUnitsService.expenses(unitId as string, periodId, signal),
    enabled: Boolean(unitId),
  });
}

export function useExtraordinaryExpenses(unitId: string | undefined, periodId: string | undefined) {
  return useQuery({
    queryKey: ['extraordinary-expenses', unitId ?? '', periodId ?? ''],
    queryFn: ({ signal }) =>
      extraordinaryExpensesService.reconciliation(unitId as string, periodId as string, signal),
    enabled: Boolean(unitId && periodId),
  });
}

export function useCreateExtraordinaryExpense() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ unitId, input }: { unitId: string; input: ExtraordinaryExpenseInput }) =>
      extraordinaryExpensesService.create(unitId, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['extraordinary-expenses'] });
      client.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

export function useUpdateExtraordinaryExpense() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ExtraordinaryExpenseUpdateInput }) =>
      extraordinaryExpensesService.update(id, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['extraordinary-expenses'] });
      client.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

export function useDeleteExtraordinaryExpense() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => extraordinaryExpensesService.remove(id),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['extraordinary-expenses'] });
      client.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

export function useCompanyOverview(periodId?: string) {
  return useQuery({
    queryKey: queryKeys.company(periodId),
    queryFn: ({ signal }) => companyService.overview(periodId, signal),
  });
}

export function useImports() {
  return useQuery({
    queryKey: queryKeys.imports,
    queryFn: ({ signal }) => importsService.list(signal),
  });
}

export function useImportDetail(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.importDetail(id ?? ''),
    queryFn: ({ signal }) => importsService.detail(id as string, signal),
    enabled: Boolean(id),
  });
}

export function useImportQuality(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.importQuality(id ?? ''),
    queryFn: ({ signal }) => importsService.quality(id as string, signal),
    enabled: Boolean(id),
  });
}

export function useImportChanges(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.importChanges(id ?? ''),
    queryFn: ({ signal }) => importsService.changes(id as string, signal),
    enabled: Boolean(id),
  });
}

export function useReopenPeriod() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ periodId, reason }: { periodId: string; reason: string }) =>
      periodsService.reopen(periodId, reason),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.periods }),
  });
}

export function useClosePeriod() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ periodId, note }: { periodId: string; note?: string }) =>
      periodsService.close(periodId, note),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.periods }),
  });
}

export function useUploadImport() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => importsService.upload(file),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.imports }),
  });
}

export function useConfirmImport() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => importsService.confirm(id),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.imports });
      client.invalidateQueries({ queryKey: ['kpis'] });
      client.invalidateQueries({ queryKey: ['pnl'] });
      client.invalidateQueries({ queryKey: ['expenses'] });
      client.invalidateQueries({ queryKey: ['company'] });
      client.invalidateQueries({ queryKey: queryKeys.periods });
    },
  });
}

export function useValueDetail(unitId?: string, categoryId?: string, periodId?: string) {
  return useQuery({
    queryKey: ['value-detail', unitId, categoryId, periodId],
    queryFn: () => valuesService.detail(unitId as string, categoryId as string, periodId as string),
    enabled: Boolean(unitId && categoryId && periodId),
  });
}

export function useAlerts(periodId?: string) {
  return useQuery({
    queryKey: ['alerts', periodId ?? 'latest'],
    queryFn: () => alertsService.list(periodId),
  });
}

/** Owner-only. `enabled` lets a caller refuse to fetch at all when the user
 * may not read the log — hiding the menu is not the protection, not asking
 * for the data is (and the server refuses it independently). */
export function useAudit(
  params: { action?: string; entity_type?: string; offset?: number },
  enabled = true,
) {
  return useQuery({
    queryKey: ['audit', params],
    queryFn: () => auditService.list({ limit: 50, ...params }),
    enabled,
  });
}

export function useComments(params: {
  business_unit_id?: string;
  period_id?: string;
  category_id?: string;
  import_id?: string;
}) {
  return useQuery({
    queryKey: ['comments', params],
    queryFn: () => commentsService.list(params),
    enabled: Object.values(params).some(Boolean),
  });
}

export function useCreateComment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: commentsService.create,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['comments'] });
      client.invalidateQueries({ queryKey: ['value-detail'] });
      client.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

export function useCreateAdjustment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: adjustmentsService.create,
    onSuccess: () => {
      // The effective value changed: every financial read is stale.
      client.invalidateQueries({ queryKey: ['kpis'] });
      client.invalidateQueries({ queryKey: ['pnl'] });
      client.invalidateQueries({ queryKey: ['expenses'] });
      client.invalidateQueries({ queryKey: ['company'] });
      client.invalidateQueries({ queryKey: ['value-detail'] });
      client.invalidateQueries({ queryKey: ['alerts'] });
      client.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

export function useRollbackImport() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => importsService.rollback(id),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.imports });
      client.invalidateQueries({ queryKey: ['kpis'] });
      client.invalidateQueries({ queryKey: ['pnl'] });
      client.invalidateQueries({ queryKey: ['expenses'] });
      client.invalidateQueries({ queryKey: ['company'] });
      client.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

/** Owner-only re-import of a rolled-back import's original file. */
export function useReimportImport() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => importsService.reimport(id),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.imports });
      client.invalidateQueries({ queryKey: ['kpis'] });
      client.invalidateQueries({ queryKey: ['pnl'] });
      client.invalidateQueries({ queryKey: ['pnl-source'] });
      client.invalidateQueries({ queryKey: ['expenses'] });
      client.invalidateQueries({ queryKey: ['company'] });
      client.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

export function useCategories() {
  return useQuery({
    queryKey: queryKeys.categories,
    queryFn: ({ signal }) => categoriesService.list(signal),
    staleTime: 10 * 60_000,
  });
}

export function useUnmappedRows(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.importUnmapped(id ?? ''),
    queryFn: ({ signal }) => importsService.unmapped(id as string, signal),
    enabled: Boolean(id),
  });
}

export function useMapSheet() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, sheetId, businessUnitId }: { id: string; sheetId: string; businessUnitId: string }) =>
      importsService.mapSheet(id, sheetId, businessUnitId),
    onSuccess: (_data, { id }) => {
      client.invalidateQueries({ queryKey: queryKeys.importDetail(id) });
      client.invalidateQueries({ queryKey: queryKeys.importQuality(id) });
      client.invalidateQueries({ queryKey: queryKeys.importChanges(id) });
      client.invalidateQueries({ queryKey: queryKeys.importUnmapped(id) });
    },
  });
}

export function useMapCategory() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, rawLabel, categoryId }: { id: string; rawLabel: string; categoryId: string }) =>
      importsService.mapCategory(id, rawLabel, categoryId),
    onSuccess: (_data, { id }) => {
      client.invalidateQueries({ queryKey: queryKeys.importDetail(id) });
      client.invalidateQueries({ queryKey: queryKeys.importQuality(id) });
      client.invalidateQueries({ queryKey: queryKeys.importChanges(id) });
      client.invalidateQueries({ queryKey: queryKeys.importUnmapped(id) });
    },
  });
}
